import { z } from "zod";

import { IsoTimestampSchema, ReferenceIdSchema, UUIDSchema } from "./shared";

export const MemorisationStatusSchema = z.enum(["new", "learning", "reviewing", "memorised"]);
export const MemorisationTargetTypeSchema = z.enum(["personal_dua", "library"]);

const MemorisationMutableObject = z.object({
  targetType: MemorisationTargetTypeSchema,
  targetId: ReferenceIdSchema,
  status: MemorisationStatusSchema.default("new"),
  repetitions: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).default(0),
  lapses: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).default(0),
  easeFactor: z.number().finite().min(1.3).max(5).default(2.5),
  intervalDays: z.number().int().nonnegative().max(36_500).default(0),
  nextReviewAt: IsoTimestampSchema.optional(),
  lastReviewedAt: IsoTimestampSchema.optional(),
  lineProgress: z.number().finite().min(0).max(1).default(0),
});

function validateMemorisationTarget(
  state: { targetType: z.infer<typeof MemorisationTargetTypeSchema>; targetId: string },
  context: z.RefinementCtx,
): void {
  if (state.targetType === "personal_dua" && !UUIDSchema.safeParse(state.targetId).success) {
    context.addIssue({
      code: "custom",
      message: "A personal du'a memorisation target must be a UUID.",
      path: ["targetId"],
    });
  }
}

export const MemorisationStateCreateSchema = MemorisationMutableObject.strict().superRefine(
  validateMemorisationTarget,
);

export const MemorisationStateSchema = MemorisationMutableObject.extend({
  id: UUIDSchema,
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  deletedAt: IsoTimestampSchema.optional(),
})
  .strict()
  .superRefine(validateMemorisationTarget);

export const MemorisationStateUpdateSchema = z
  .object({
    status: MemorisationStatusSchema.optional(),
    repetitions: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
    lapses: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
    easeFactor: z.number().finite().min(1.3).max(5).optional(),
    intervalDays: z.number().int().nonnegative().max(36_500).optional(),
    nextReviewAt: IsoTimestampSchema.optional(),
    lastReviewedAt: IsoTimestampSchema.optional(),
    lineProgress: z.number().finite().min(0).max(1).optional(),
  })
  .strict();

export type MemorisationStatus = z.infer<typeof MemorisationStatusSchema>;
export type MemorisationState = z.output<typeof MemorisationStateSchema>;
export type CreateMemorisationStateInput = z.input<typeof MemorisationStateCreateSchema>;
export type UpdateMemorisationStateInput = z.input<typeof MemorisationStateUpdateSchema>;
